import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { USER_TYPE } from '../../../../../utils/constants/user';
import CategorySelector from '../../../../../common/CategorySelector/CategorySelector';
import DealsTable from './DealsTable/DealsTable';
import EmptyDeals from '../EmptyDeals/EmptyDeals';
import LoadingSpinner from '../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import SearchBox from '../../../Portfolio/components/CompaniesList/components/SearchBox/SearchBox';
import styles from './DealsList.module.scss';
import { useDealsList } from './useDealsList';
import { Button } from '../../../../../common/_custom';
import ColumnCustomizationButton from '../../../../../common/_custom/ColumnCustomizationButton';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { setCustomFilterDrawer, setColumnCustomizationState, setMobileOverviewSource } from '../../../../../store/investor/action';
import { debtProdFilters, GREY_DROPDOWN_CARET, PURPLE_CROSS } from '../../../../../assets/hostedassets';
import { isAppliedFiltersEmpty, isLenderType } from '../../../../../utils/utils';
import { useNavigate, useParams } from 'react-router-dom';
import { DealStatus } from '../../../../../store/investor/reducer';
import { COLORS } from '../../../../../utils/constants/colors';
import { DATE_FORMATS, formatDateByFormat } from '../../../../../utils/dateUtils';
import FilteredViewsButton from '../../../../../common/FilteredViewsButton/FilteredViewsButton';
import useClearFilteredView from '../../../../../common/FilteredViewsButton/useClearFilteredView';
import { useDataTableExport } from '../../../../../common/_custom/DataTable/utils/useDataTableExport';
import { fetchAllDealsForExport } from './dealsExportService';
import { useIsMobile } from '../../../../../common/_custom/Hooks/useIsMobile';
import MobileHeader from '../../../../../common/MobileLayout/MobileHeader';
import MobileBottomTabs from '../../../../../common/MobileLayout/MobileBottomTabs';
import MobileViewSheet from '../../../Portfolio/components/CompaniesList/components/MobileViewSheet/MobileViewSheet';
import DealCardList from './components/DealCard/DealCardList';
// DataTableFilterDrawer is rendered by the hidden DealsTable below
import { DEAL_VIEW_TYPES, DEAL_COLUMN_KEYS } from '../../../../../store/investor/constants';
import { VIEW_CONFIGS } from '../../../../../utils/constants/commonConstants';
import { getDealFormConfig } from '../../../../../store/investor/apiHelpers';
import ThreeDotMenu from '../../../../../common/ThreeDotMenu/ThreeDotMenu';
import { buildCtaActionMenuItems, EXPORT_XLSX_ACTION_KEY } from '../../../../../utils/constants/ctaActionMenu';
import {
    portfolioDetailPath,
    companyPortfolioDetailPath,
    INVESTOR_ROUTES,
    COMPANY_ROUTES,
} from '../../../../../utils/constants/routesConst';

// The global Deals listing (`/investor/app/deals`) — mirrors GLOBAL_DEALS_PATH in
// notificationNavigation.ts, composed from the same existing route segments.
const INVESTOR_DEALS_PATH = `${INVESTOR_ROUTES.APP}/${INVESTOR_ROUTES.DEALS}`;

export const fallbackDealStatusOption = {
    [DealStatus.SHARED]: {
        label: 'Deal Shared',
        value: DealStatus.SHARED,
        backgroundColor: COLORS.PURPLE_TINT_50,
        labelColor: 'var(--primary-text-colour)',
        customCaret: {
            height: '14',
            width: '14',
            colour: 'var(--primary-text-colour)',
        },
    },
    Default: {
        label: '-',
        value: '',
        backgroundColor: 'transparent',
        labelColor: '#000',
        customCaret: {
            height: '14',
            width: '14',
            colour: '#000',
        },
    },
    Select: {
        label: 'Select',
        value: '-',
        backgroundColor: COLORS.LIGHT_BG,
        labelColor: COLORS.GREY,
        customCaret: GREY_DROPDOWN_CARET,
    },
};

const DealsList: React.FC<any> = ({ selectedCompany }) => {
    const {
        fetchingDeals,
        dealsData,
        userType,
        displayedOrgList,
        selectedCategory,
        selectedOrgId,
        selectedLenderId,
        accessTierParam,
        LENGTH,
        CATEGORY_OPTIONS,
        setScrollPos,
        getTotalCount,
        changeSelectedTab,
        changePageNumber,
        clearMobileDealList,
        resetMobileDealListIfFiltersChanged,
        handleSearch,
        handleOrgSelect,
        redirectToPreferences,
        sortConfig,
        handleSort,
        appliedFilters,
        currentColumns,
        dealsColumnsConfig,
        setDealsColumnsConfig,
    } = useDealsList();

    // Get the correct column configuration based on selected category.
    // dealViewHeaders/mandateHeaders from the AICA_DEAL_NAVBAR_VIEW config are
    // { columns: DataTableColumn[], pinnedColumns: [...] } — NOT a plain array.
    // DealsRow.tsx (the on-screen render path) already unwraps `.columns` before
    // passing to DataTable's masterColumnConfig prop; this must match, or the
    // export sees a truthy-but-non-array value and produces zero columns
    // (confirmed via live testing).
    const getMasterColumnConfig = () => {
        if (!dealsColumnsConfig) return [];

        if (selectedCategory === 'DEAL_VIEW') {
            return dealsColumnsConfig.dealViewHeaders?.columns || [];
        } else if (selectedCategory === 'MANDATE_VIEW') {
            return dealsColumnsConfig.mandateHeaders?.columns || [];
        } else {
            // Default to deal view headers
            return dealsColumnsConfig.dealViewHeaders?.columns || [];
        }
    };

    // Stable identity across renders (only recomputes when the config actually loads/
    // changes) — getMasterColumnConfig()'s `|| []` otherwise returns a fresh array every
    // render, which changes handleExport's identity on every DealsList render.
    //
    // The `dealStatus` column (SINGLE_SELECT_DROPDOWN) has no `dropdownOptions` in the
    // API config — on screen its label is resolved separately via
    // dealStatusConfig.statusDropdownOptionsMap (see DataTable.tsx). The export path
    // (getExportCellValue) only reads column.dropdownOptions, so without this enrichment
    // the Status column exports blank. statusDropdownOptionsMap is already keyed by the
    // raw dealStatus value with the same { label, value, ... } shape dropdownOptions expects.
    // Fall back to `{}` (never omit dropdownOptions) when the map isn't loaded yet, so
    // getExportCellValue's "value not found in map" branch still exports the raw
    // dealStatus value instead of a blank cell.
    const masterColumnConfig = useMemo(() => {
        const columns = getMasterColumnConfig();
        const statusDropdownOptionsMap = dealsData?.dealStatusConfig?.statusDropdownOptionsMap || {};

        return columns.map((column: any) =>
            column.field === 'dealStatus' && !column.dropdownOptions ? { ...column, dropdownOptions: statusDropdownOptionsMap } : column,
        );
    }, [dealsColumnsConfig, selectedCategory, dealsData?.dealStatusConfig]);

    const LoadingTableData = <LoadingSpinner color="var(--primary-text-colour)" height="40px" />;

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    // Mobile header/toolbar refs for the scroll-border observer below
    const headerSentinelRef = useRef<HTMLDivElement>(null);
    const mobileTopRef = useRef<HTMLDivElement>(null);
    const params = useParams();
    const isCompanyDashboardDealsPage = !!params.id;

    // Check if user is Lender or Partner (should not see Select View functionality)
    const isLenderUser = isLenderType(userType);
    const isPartnerUser = userType === USER_TYPE.PARTNER;
    const isCompanyUser = userType === USER_TYPE.COMPANY;

    const shouldHideViewSelector = isLenderUser || isPartnerUser;

    // Use the custom hook for clearing filtered views
    const { clearFilteredView } = useClearFilteredView({
        source: 'DEALS',
        filterSourceKey: 'DEALS',
    });

    const customFilterDrawer = useAppSelector((state) => state.investor.customFilterDrawer);

    const isMobile = useIsMobile();
    const [selectedMobileView, setSelectedMobileView] = useState<any>(null);
    const hasActiveFilters = !isAppliedFiltersEmpty(appliedFilters);
    const userData = useAppSelector((state) => state.user.userData);
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    // Export CTA in the Deals kebab is config-driven: the EXPORT_XLSX key is seeded per role
    // under the AICA_DASHBOARD `actionConfig.dealsCtaAction` (godzilla, investorAccess doc) —
    // the same mechanism and doc as Leads' leadsCtaAction. That config is loaded at investor
    // bootstrap and survives the Deals page's (empty) AICA_LENDER_DASHBOARD merge, so it's
    // readable here. The config array carries the key; the FE maps it to its label.
    const dashboardConfig = useAppSelector((state) => state.dashboard.dashboardReducer.config);
    const dealsCtaAction = dashboardConfig?.actionConfig?.dealsCtaAction ?? [];

    // Export is wired directly into the Deals toolbar's kebab (3-dot) menu below.
    const { handleExport, isExporting } = useDataTableExport({
        source: 'Deals',
        filterSourceKey: 'DEALS',
        masterColumnConfig,
        columnValueGetters: {
            // Company Name: mirror the on-screen valueGetter (DealsRow) — a Mandate child
            // (investor sub-row) shows the lender name, not the parent's borrower name;
            // borrower-side users don't see lender names (matches DealsRow's isBorrower guard).
            borrowerName: (row: any) => (row?._isCollapsibleRow ? (isCompanyUser ? '-' : row?.lenderName) : row?.borrowerName) ?? '',
            // "Probability" is a COMPONENT column (ProbabilityBar on screen) so the generic
            // export path skips it — resolve it here from the raw debtOptionProbability,
            // mirroring the on-screen display (non-numeric/<=0 -> 'N/A', else '<value>%', clamped 0-100).
            [DEAL_COLUMN_KEYS.DEAL_PROBABILITY]: (row: any) => {
                const p = parseFloat(row?.customFields?.debtOptionProbability);
                return Number.isNaN(p) || p <= 0 ? 'N/A' : `${Math.max(0, Math.min(100, p))}%`;
            },
        },
        fetchAllRows: () =>
            fetchAllDealsForExport({
                userType,
                userData,
                platformData,
                selectedCategory,
                selectedLenderId,
                selectedOrgId,
                companyDashboardId: params.id,
                appliedFilters,
                sortConfig,
            }),
        getFileName: () => `Deals_${selectedCategory}_${formatDateByFormat(Date.now(), DATE_FORMATS.ISO)}.xlsx`,
        sheetName: 'Deals',
    });

    // Fetch deal columns config on mobile (desktop gets it from DealsTable).
    // Fetch-once: `dealsColumnsConfig` is read as a guard but intentionally kept
    // out of the dep array so setting it doesn't retrigger the effect.
    useEffect(() => {
        if (!isMobile || dealsColumnsConfig || !platformData?.investorOrgId) return;
        getDealFormConfig(
            {
                accountType: userData?.accountType,
                investorOrgId: platformData?.investorOrgId,
                primaryEmail: userData?.primaryEmail,
                configName: VIEW_CONFIGS.AICA_DEAL_NAVBAR_VIEW,
            },
            {
                onSuccess: (configData: any) => {
                    setDealsColumnsConfig(configData);
                },
                onError: () => {},
            },
        ).catch(() => {});
    }, [isMobile, platformData?.investorOrgId]);

    const applyMobileViewFilters = useCallback(
        (filters: Record<string, any>) => {
            resetMobileDealListIfFiltersChanged(filters);
            dispatch(
                setCustomFilterDrawer({
                    open: false,
                    appliedFilters: { DEALS: filters },
                }),
            );
        },
        [resetMobileDealListIfFiltersChanged, dispatch],
    );

    const clearAllFiltersAndView = useCallback(() => {
        resetMobileDealListIfFiltersChanged({});
        clearFilteredView();
        setSelectedMobileView(null);
        dispatch(
            setCustomFilterDrawer({
                open: false,
                appliedFilters: { DEALS: {} },
            }),
        );
    }, [resetMobileDealListIfFiltersChanged, clearFilteredView, dispatch]);

    const handleViewSelect = useCallback(
        (filters: any, columns?: string[]) => {
            const sharedSourceKey = 'DEALS';
            dispatch(
                setColumnCustomizationState({
                    sourceKey: sharedSourceKey,
                    selectedColumns: columns && columns.length > 0 ? columns : [],
                    tempSelectedColumns: columns && columns.length > 0 ? columns : [],
                    isModified: false,
                }),
            );
        },
        [dispatch],
    );

    const kebabMenuItems = useMemo(
        () =>
            buildCtaActionMenuItems(dealsCtaAction, {
                [EXPORT_XLSX_ACTION_KEY]: {
                    // Guarded no-op while an export is already running, so a second
                    // click during export can't re-fire the handler.
                    onClick: isExporting ? () => {} : handleExport,
                    label: isExporting ? <LoadingSpinner height="16px" color="var(--primary-text-colour)" /> : undefined,
                    keepOpenOnClick: true,
                },
            }),
        [dealsCtaAction, handleExport, isExporting],
    );

    const cardListKey = useMemo(
        () =>
            `${selectedCategory}-${JSON.stringify(appliedFilters)}-${selectedMobileView?.value || ''}-${selectedOrgId || ''}-${
                selectedLenderId || ''
            }`,
        [selectedCategory, appliedFilters, selectedMobileView?.value, selectedOrgId, selectedLenderId],
    );

    const NoResultComponent = (
        <EmptyDeals
            handleClick={redirectToPreferences}
            selectedCompany={selectedCompany}
            filterApplied={!isAppliedFiltersEmpty(appliedFilters)}
            onClearFilters={clearAllFiltersAndView}
        />
    );
    // On mobile, always start from page 1 — infinite scroll handles pagination locally.
    // Without this, a refresh keeps the stale ?page=N param and fetches a mid-list page.
    useEffect(() => {
        if (!isMobile) return;
        changePageNumber(1);
    }, [isMobile]);

    useEffect(() => {
        if (!isMobile) return;
        const sentinel = headerSentinelRef.current;
        const toolbar = mobileTopRef.current;
        if (!sentinel || !toolbar) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                toolbar.style.borderBottomColor = entry.isIntersecting ? 'transparent' : 'var(--border-color-new)';
            },
            { threshold: 0 },
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [isMobile]);

    if (isMobile) {
        return (
            <div className={styles.DealsList}>
                <MobileHeader />
                <div ref={headerSentinelRef} />
                <div ref={mobileTopRef} className={styles.MobileTop}>
                    <div className={styles.MobileTopRow}>
                        <div className={styles.Label}>{isCompanyUser ? 'Loans' : 'Deals'}</div>
                        <div className={styles.MobileActions}>
                            {!shouldHideViewSelector && (
                                <MobileViewSheet
                                    source={selectedCategory}
                                    selectedView={selectedMobileView}
                                    onViewSelect={handleViewSelect}
                                    onSelectedViewChange={setSelectedMobileView}
                                    onFiltersApply={applyMobileViewFilters}
                                    onFilterValuesUpdate={applyMobileViewFilters}
                                    onClear={clearAllFiltersAndView}
                                />
                            )}
                            <Button
                                text={''}
                                onClick={() => {
                                    dispatch(setCustomFilterDrawer({ open: true }));
                                }}
                                icon={debtProdFilters}
                                style={{
                                    marginTop: 0,
                                    borderRadius: '0.25rem',
                                    height: '2.5rem',
                                    width: '2.5rem',
                                    minWidth: '2.5rem',
                                    padding: '0',
                                    gap: '0',
                                    ...(hasActiveFilters
                                        ? {
                                              background: 'var(--primary-purple-hover-bg)',
                                              border: '1px solid var(--primary-purple-hover-bg)',
                                              width: 'auto',
                                              minWidth: 'auto',
                                              padding: undefined,
                                              gap: '0.375rem',
                                          }
                                        : {}),
                                }}
                                variant={'secondary'}
                                endIcon={hasActiveFilters ? PURPLE_CROSS : null}
                                endIconStyles={{ height: '0.8rem', marginLeft: '0.25rem' }}
                                iconStyles={
                                    hasActiveFilters
                                        ? { paddingRight: '0.5rem', borderRight: '1px solid var(--border-color)', width: '1.5rem' }
                                        : {
                                              margin: '0 auto',
                                              display: 'block',
                                              filter: 'brightness(0) saturate(100%) invert(29%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%)',
                                          }
                                }
                                endIconClick={clearAllFiltersAndView}
                            />
                            <SearchBox
                                onSearch={handleSearch}
                                categoryParam={''}
                                location={location}
                                accessTierParam={accessTierParam || ''}
                                placeholder="Search"
                                showDropdown={true}
                                dropdownOptions={displayedOrgList.map((org) => ({
                                    id: org.assesseeOrgId,
                                    label: org.orgName,
                                }))}
                                onOptionSelect={handleOrgSelect}
                            />
                        </div>
                    </div>
                    <div className={styles.MobileCategoryTabs}>
                        <CategorySelector
                            categoryOptions={CATEGORY_OPTIONS}
                            selectedCategory={selectedCategory}
                            changeSelectedTab={changeSelectedTab}
                        />
                    </div>
                </div>

                <DealCardList
                    key={cardListKey}
                    deals={dealsData.data || []}
                    isLoading={fetchingDeals}
                    hasNextPage={dealsData.hasNextPage}
                    dealStatusConfig={dealsData.dealStatusConfig}
                    selectedCategory={selectedCategory}
                    currentPageNumber={dealsData.pageNumber}
                    onLoadMore={() => changePageNumber(dealsData.pageNumber + 1)}
                    onDealClick={(deal) => {
                        // Company/borrower sessions live under /company/** and can't reach the
                        // /investor/app/** route tree — route (and the back-nav "from" source) by role
                        // so a company user's loan card lands on their own Overview + back button.
                        dispatch(setMobileOverviewSource(isCompanyUser ? COMPANY_ROUTES.DEALS : INVESTOR_DEALS_PATH));
                        navigate(
                            isCompanyUser
                                ? companyPortfolioDetailPath(deal.dealBeneficiaryId)
                                : portfolioDetailPath(deal.dealBeneficiaryId),
                        );
                    }}
                    emptyComponent={NoResultComponent}
                />

                <MobileBottomTabs />

                {/* Off-screen only so its DataTableFilterDrawer works — the SideDrawer inside is
                    position:fixed and escapes this container. */}
                <div className={styles.OffscreenTable}>
                    <DealsTable
                        filterDrawerOnly
                        onBeforeFiltersApply={clearMobileDealList}
                        isLoading={false}
                        emptyScreenComponent={null}
                        loadingComponent={null}
                        setScrollPos={() => {}}
                        selectedCategory={selectedCategory}
                        sortConfig={sortConfig}
                        onSort={() => {}}
                        currentColumns={currentColumns}
                        onColumnsChange={() => {}}
                        onColumnsConfigChange={(config) => setDealsColumnsConfig(config)}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.DealsList}>
            <div className={styles.Top}>
                <div className={styles.LeftSection}>
                    <div className={styles.SectionTitle}>{isCompanyUser ? 'Loans' : 'Deals'}</div>
                    {![USER_TYPE.LENDER, USER_TYPE.LENDER_TEAM].includes(userType) && (
                        <div className={styles.CategorySelector}>
                            <CategorySelector
                                categoryOptions={CATEGORY_OPTIONS}
                                selectedCategory={selectedCategory}
                                changeSelectedTab={changeSelectedTab}
                            />
                        </div>
                    )}
                </div>
                <div className={styles.RightSection}>
                    {!isLenderType(userType) && !isCompanyDashboardDealsPage && !isCompanyUser && (
                        <SearchBox
                            onSearch={handleSearch}
                            categoryParam={''}
                            location={location}
                            accessTierParam={accessTierParam || ''}
                            showDropdown={true}
                            dropdownOptions={displayedOrgList.map((org) => ({
                                id: org.assesseeOrgId,
                                label: org.orgName,
                            }))}
                            onOptionSelect={handleOrgSelect}
                        />
                    )}
                    <FilteredViewsButton
                        source={selectedCategory}
                        appliedFilters={appliedFilters}
                        currentColumns={currentColumns}
                        onViewSelect={handleViewSelect}
                        masterColumnConfig={masterColumnConfig}
                    />
                    <Button
                        text={''}
                        onClick={() => {
                            dispatch(
                                setCustomFilterDrawer({
                                    open: true,
                                }),
                            );
                        }}
                        icon={debtProdFilters}
                        style={{
                            marginTop: 0,
                            borderRadius: '0.25rem',
                            height: '2.75rem',
                            ...(isAppliedFiltersEmpty(appliedFilters)
                                ? {}
                                : {
                                      background: '#F2E8FF',
                                      border: '1px solid #F2E8FF',
                                  }),
                        }}
                        variant={'purpleinverted'}
                        iconStyles={
                            !isAppliedFiltersEmpty(appliedFilters)
                                ? { paddingRight: '0.5rem', borderRight: '1px solid #C7C7C7', width: '1.5rem' }
                                : { margin: '0' }
                        }
                        endIcon={!isAppliedFiltersEmpty(appliedFilters) ? PURPLE_CROSS : null}
                        endIconClick={() => {
                            // Clear filters and filtered view using the custom hook
                            clearFilteredView();
                        }}
                    />
                    <ColumnCustomizationButton
                        enabled={true}
                        filterSourceKey="DEALS"
                        currentColumns={currentColumns}
                        areColumnsCustomized={currentColumns.length > 0}
                        variant="secondary"
                        onCustomColumnsCleared={() => {
                            // Update Redux state to clear column customization
                            dispatch(
                                setColumnCustomizationState({
                                    sourceKey: 'DEALS',
                                    selectedColumns: [],
                                    tempSelectedColumns: [],
                                    isModified: false,
                                }),
                            );
                        }}
                        masterColumnConfig={masterColumnConfig}
                    />
                    <ThreeDotMenu menuItems={kebabMenuItems} tableName="CtaActionMenu" />
                </div>
            </div>
            <div className={styles.TableContainer}>
                <DealsTable
                    isLoading={fetchingDeals}
                    emptyScreenComponent={NoResultComponent}
                    loadingComponent={LoadingTableData}
                    setScrollPos={setScrollPos}
                    selectedCategory={selectedCategory}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    currentColumns={currentColumns}
                    onColumnsChange={(columns) => {
                        // Update Redux state for column customization
                        dispatch(
                            setColumnCustomizationState({
                                sourceKey: 'DEALS',
                                selectedColumns: columns.map((col) => col.field),
                                tempSelectedColumns: columns.map((col) => col.field),
                                isModified: false,
                            }),
                        );
                    }}
                    onColumnsConfigChange={(config) => {
                        setDealsColumnsConfig(config);
                    }}
                />

                <div className={styles.PageNumber}>
                    <div>
                        {`Showing ${getTotalCount() > 0 && dealsData?.pageNumber > 0 ? LENGTH * (dealsData.pageNumber - 1) + 1 : 0} - ${
                            getTotalCount() > 0 && dealsData?.pageNumber > 0 ? Math.min(LENGTH * dealsData.pageNumber, getTotalCount()) : 0
                        } of ${getTotalCount() || 0}`}
                    </div>

                    <div>
                        <img src="https://fl-fe-assets.s3.ap-south-1.amazonaws.com/svg/poweredByAica.svg" alt="" width={'150px'} />
                    </div>
                    <div className={styles.Buttons}>
                        <div
                            className={clsx(styles.Btn, {
                                [styles.Disabled]: !(dealsData.pageNumber > 1),
                            })}
                            onClick={() => {
                                changePageNumber(dealsData.pageNumber - 1);
                            }}
                        >
                            Previous
                        </div>
                        <div
                            className={clsx(styles.Btn, {
                                [styles.Disabled]: !dealsData?.hasNextPage,
                            })}
                            onClick={() => {
                                changePageNumber(dealsData.pageNumber + 1);
                            }}
                        >
                            Next
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DealsList;
